/**
 * ownershipCue.ts — the 3-way, VIEWER-RELATIVE ownership map cue (feat/ownership-coordination).
 *
 * COMMAND is exclusive; the PICTURE is shared. Every COP operator must see WHO holds Incident
 * Command of a hazard — and whether it's THEIRS — without the cue ever dimming a still-burning
 * hazard. So the cue is an ADDITIVE keyline baked into the disc canvas (discGlyphComposite):
 *
 *   • UN-OWNED           → no ring. ★ The ABSENCE is the alarm — this is the orphan, the state
 *                          that kills (a Threat-Level-≥3 hazard nobody has assumed command of).
 *   • OWNED BY ANOTHER   → a steel keyline ring.
 *   • OWNED BY ME        → the same ring + a distinguishing white inner keyline.
 *
 * ★ HARD RULES (pinned by fireRenderModel.lock + colourLaw.deuteranopia):
 *   - the cue NEVER touches fill / size / opacity (an owned hazard is STILL BURNING);
 *   - no second motion channel (pulse is spent on "unacked urgent" — ownership silences the
 *     pulse via the existing worked/ack path, it never ADDS motion);
 *   - the mine-vs-theirs relation is SERVER-computed (owned_incident.viewer_is_lead) because under
 *     AUTH-off the client has no agency id.
 */
import type { OwnedIncidentRef } from '@tsr/shared-types';
import { ACK_STEEL } from '../layers/anomalyMarker';
import { DISC_GLYPH_INK, type DiscOwnershipRing } from '../discGlyphComposite';

/**
 * The ownership keyline colour (BOTH mine & other) — steel: "command established, handled by a
 * party." It is a SHAPE cue — its PRESENCE signals ownership, so colour is redundant and the
 * cue is deuteranopia-safe (colourLaw.deuteranopia asserts contrast vs the globe backdrop). Reuses
 * ACK_STEEL, the platform's established "handled/acknowledged" steel, so the semantics cohere.
 */
export const OWNERSHIP_RING_COLOR = ACK_STEEL;
/**
 * The "OWNED BY ME" distinguishing inner keyline — white. White sits far above every disc fill
 * (L1–L4), ACK_STEEL, and the OUT grey in luminance, so mine-vs-theirs separates for a deuteranope
 * by LUMINANCE as well as by shape. Reuses DISC_GLYPH_INK (already pinned ≥1.5 on all fills + steel).
 */
export const OWNERSHIP_MINE_INNER_COLOR = DISC_GLYPH_INK;

/**
 * Resolve the 3-way ownership ring spec for the disc compositor, or null when un-owned (no cue).
 * Reads the thin owned_incident ref: presence ⇒ a LIVE command lead exists (owned/response
 * lifecycle — a closed incident's ref is already null), and the server-computed viewer_is_lead
 * distinguishes mine from theirs. Pure + exported so the decision is unit-locked.
 */
export function ownershipRing(
  owned: OwnedIncidentRef | null | undefined,
): DiscOwnershipRing | null {
  if (!owned) return null;
  return owned.viewer_is_lead
    ? { color: OWNERSHIP_RING_COLOR, inner: OWNERSHIP_MINE_INNER_COLOR }
    : { color: OWNERSHIP_RING_COLOR };
}
